import { FeatureFlagName, type FeatureFlags } from "./featureFlags";

const BACKUP_TIMEOUT_MS = 5000;

/**
 * The utility's last-known flags, which the backend republishes as a static file on every
 * flag save (see "Backup snapshot" in ai-docs/feature-flag-implementation-guide.md). Read
 * only when Flagsmith cannot answer. `backupUrl` is the public container, no trailing slash
 * (see resolveFlagsmithSettings); empty turns the fallback off. `undefined` when there is no
 * usable backup either, which reads as everything off.
 */
export async function loadFlagBackup(utilityId: string, backupUrl: string): Promise<FeatureFlags | undefined> {
  if (!backupUrl) return undefined;

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), BACKUP_TIMEOUT_MS);

  try {
    // no-store: the file keeps its name across saves, so a cached copy may be an old one.
    const response = await fetch(`${backupUrl}/${utilityId}.json`, {
      cache: "no-store",
      signal: abort.signal,
    });
    if (!response.ok) return undefined;

    const backup = await response.json();

    // Only a backup this code understands, written for this utility, with flags in it.
    if (backup?.schemaVersion !== 1 || backup.utilityId !== utilityId || !isFlagMap(backup.flags)) {
      return undefined;
    }

    // Case-insensitive, the way the SDK's hasFeature() compares names.
    const values = new Map(
      Object.entries(backup.flags).map(([name, enabled]) => [name.toLowerCase(), enabled]),
    );
    return Object.fromEntries(
      Object.values(FeatureFlagName).map(name => [name, values.get(name.toLowerCase()) === true]),
    );
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

function isFlagMap(value: unknown): value is Record<string, boolean> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.keys(value).length > 0 &&
    Object.values(value).every(enabled => typeof enabled === "boolean")
  );
}
